// =============================================================================
// PAGES/ADMINETAPASESTEIRA.TSX — Gerenciador de Etapas de Sub-esteira (ADMIN)
// =============================================================================
// Integrações e Sistema → Sistema → "Etapas das Esteiras".
// Deixa o admin criar/editar/mover/desabilitar as colunas (etapas) de cada
// macro-fase. Plano: knowledge/.../gerenciador-etapas-plano-03-11-2026.md.
//
// Acoplamento subStatus × ordem: o reorder no back remapeia as operações por FK
// (a op segue a etapa por identidade). Aqui só mandamos a lista completa de ids
// na nova ordem. D1: COMERCIAL entra, com aviso de board pendente.
// =============================================================================

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  ListOrdered,
  GripVertical,
  Plus,
  Pencil,
  EyeOff,
  Eye,
  Trash2,
  Flag,
  Asterisk,
} from 'lucide-react';
import { Sheet, SheetContent } from '../components/ui/sheet';
import ConfirmModal from '../components/ConfirmModal';
import { etapasSubEsteiraApi, type EtapaSubEsteiraAdmin } from '../services/api';

const MACROFASES: { key: string; label: string; sub: string }[] = [
  { key: 'COMERCIAL', label: 'Comercial', sub: 'pré-CCV' },
  { key: 'ENTRADA', label: 'Entrada', sub: 'cadastro' },
  { key: 'CREDITO', label: 'Crédito', sub: 'análise bancária' },
  { key: 'OPERACIONAL', label: 'Operacional', sub: 'jurídico/contrato' },
  { key: 'REGISTRO', label: 'Registro', sub: 'cartório' },
];

const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 ' +
  'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-otm-navy/30 ' +
  'focus:border-otm-navy transition-colors';
const LABEL_CLS = 'block text-sm font-medium text-slate-700 mb-1';

function msgErro(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro ??
    (e instanceof Error ? e.message : fallback)
  );
}

type FormState = {
  mode: 'criar' | 'editar';
  id?: string;
  label: string;
  obrigatoria: boolean;
  terminal: boolean;
};

export default function AdminEtapasEsteira() {
  const [macroFase, setMacroFase] = useState<string>('ENTRADA');
  const [etapas, setEtapas] = useState<EtapaSubEsteiraAdmin[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [form, setForm] = useState<FormState | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [confirm, setConfirm] = useState<{
    titulo: string;
    mensagem: string;
    tom?: 'padrao' | 'perigo';
    textoConfirmar?: string;
    acao: () => Promise<void>;
  } | null>(null);
  const [processandoConfirm, setProcessandoConfirm] = useState(false);

  const faseAtual = MACROFASES.find((m) => m.key === macroFase)!;

  async function carregar(mf = macroFase) {
    setCarregando(true);
    setErro('');
    try {
      const dados = await etapasSubEsteiraApi.listarAdmin(mf);
      setEtapas(dados);
    } catch (e) {
      setErro(msgErro(e, 'Falha ao carregar etapas.'));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar(macroFase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macroFase]);

  async function onDragEnd(result: DropResult) {
    if (!result.destination || result.destination.index === result.source.index) return;
    const nova = Array.from(etapas);
    const [movida] = nova.splice(result.source.index, 1);
    nova.splice(result.destination.index, 0, movida);
    const anterior = etapas;
    setEtapas(nova); // otimista
    try {
      const atualizado = await etapasSubEsteiraApi.reordenar(
        macroFase,
        nova.map((e) => e.id),
      );
      setEtapas(atualizado);
      toast.success('Ordem atualizada. Operações foram remapeadas automaticamente.');
    } catch (e) {
      setEtapas(anterior); // rollback
      toast.error(msgErro(e, 'Falha ao reordenar.'));
    }
  }

  async function salvarForm() {
    if (!form) return;
    if (!form.label.trim()) {
      toast.error('O nome da etapa é obrigatório.');
      return;
    }
    setSalvando(true);
    try {
      if (form.mode === 'criar') {
        await etapasSubEsteiraApi.criar({
          macroFase,
          label: form.label.trim(),
          obrigatoria: form.obrigatoria,
          terminal: form.terminal,
        });
        toast.success('Etapa criada.');
      } else {
        await etapasSubEsteiraApi.editar(form.id!, {
          label: form.label.trim(),
          obrigatoria: form.obrigatoria,
          terminal: form.terminal,
        });
        toast.success('Etapa atualizada.');
      }
      setForm(null);
      await carregar();
    } catch (e) {
      toast.error(msgErro(e, 'Falha ao salvar etapa.'));
    } finally {
      setSalvando(false);
    }
  }

  function pedirDesabilitar(etapa: EtapaSubEsteiraAdmin) {
    setConfirm({
      titulo: `Desabilitar "${etapa.label}"?`,
      mensagem:
        'A etapa deixa de aparecer no quadro, mas o histórico é preservado. ' +
        'Só é possível desabilitar etapas sem operações ativas.',
      tom: 'perigo',
      textoConfirmar: 'Desabilitar',
      acao: async () => {
        await etapasSubEsteiraApi.definirAtiva(etapa.id, false);
        toast.success('Etapa desabilitada.');
        await carregar();
      },
    });
  }

  function pedirExcluir(etapa: EtapaSubEsteiraAdmin) {
    setConfirm({
      titulo: `Excluir "${etapa.label}"?`,
      mensagem:
        'A exclusão é permanente e só é permitida se nenhuma operação (de qualquer status) ' +
        'referenciar esta etapa. Caso contrário, prefira desabilitar.',
      tom: 'perigo',
      textoConfirmar: 'Excluir',
      acao: async () => {
        await etapasSubEsteiraApi.remover(etapa.id);
        toast.success('Etapa excluída.');
        await carregar();
      },
    });
  }

  async function executarConfirm() {
    if (!confirm) return;
    setProcessandoConfirm(true);
    try {
      await confirm.acao();
      setConfirm(null);
    } catch (e) {
      toast.error(msgErro(e, 'Falha na operação.'));
    } finally {
      setProcessandoConfirm(false);
    }
  }

  async function reabilitar(etapa: EtapaSubEsteiraAdmin) {
    try {
      await etapasSubEsteiraApi.definirAtiva(etapa.id, true);
      toast.success('Etapa reabilitada.');
      await carregar();
    } catch (e) {
      toast.error(msgErro(e, 'Falha ao reabilitar.'));
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-800 mb-1 flex items-center gap-2">
        <ListOrdered size={20} className="text-otm-orange" />
        Etapas das Esteiras
      </h1>
      <p className="text-sm text-slate-500 mb-5">
        Configure as colunas (etapas) de cada macro-fase do pipeline. Arraste para reordenar — as
        operações seguem a etapa automaticamente. As macro-fases em si são fixas do sistema.
      </p>

      {/* Tabs de macro-fase */}
      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-otm-border pb-3">
        {MACROFASES.map((m) => (
          <button
            key={m.key}
            onClick={() => setMacroFase(m.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              macroFase === m.key
                ? 'bg-otm-navy text-white'
                : 'text-otm-txt-2 hover:bg-otm-subtle border border-otm-border'
            }`}
          >
            {m.label}
            <span className={`ml-1.5 text-[11px] ${macroFase === m.key ? 'text-white/70' : 'text-otm-txt-3'}`}>
              {m.sub}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-4">
        <button
          onClick={() =>
            setForm({ mode: 'criar', label: '', obrigatoria: false, terminal: false })
          }
          className="text-sm px-3 py-2 bg-otm-orange text-white rounded-lg font-semibold hover:bg-otm-orange-dark transition-colors inline-flex items-center gap-1.5"
        >
          <Plus size={16} /> Nova etapa
        </button>
      </div>

      {erro && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
          {erro}
        </div>
      )}

      {carregando ? (
        <p className="text-sm text-otm-txt-3">Carregando…</p>
      ) : etapas.length === 0 ? (
        <p className="text-sm text-otm-txt-3 rounded-lg border border-dashed border-otm-border px-4 py-8 text-center">
          Nenhuma etapa cadastrada nesta macro-fase. Clique em “Nova etapa” para começar.
        </p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="etapas">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {etapas.map((etapa, index) => (
                  <Draggable key={etapa.id} draggableId={etapa.id} index={index}>
                    {(prov, snapshot) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        style={prov.draggableProps.style as React.CSSProperties}
                        className={`flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 ${
                          snapshot.isDragging ? 'border-otm-navy shadow-md' : 'border-otm-border'
                        } ${!etapa.ativa ? 'opacity-60' : ''}`}
                      >
                        <span
                          {...prov.dragHandleProps}
                          className="cursor-grab text-otm-txt-3 hover:text-otm-txt-1"
                          title="Arraste para reordenar"
                        >
                          <GripVertical size={18} />
                        </span>

                        <span className="w-7 h-7 shrink-0 rounded-full bg-otm-subtle text-otm-txt-2 text-xs font-semibold flex items-center justify-center">
                          {etapa.ordem}
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-otm-txt-1 truncate">
                              {etapa.label}
                            </span>
                            {etapa.terminal && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                                <Flag size={10} /> terminal
                              </span>
                            )}
                            {etapa.obrigatoria && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-otm-navy bg-otm-navy/10 rounded-full px-1.5 py-0.5">
                                <Asterisk size={10} /> obrigatória
                              </span>
                            )}
                            {!etapa.ativa && (
                              <span className="text-[10px] font-medium text-otm-txt-3 bg-otm-subtle border border-otm-border rounded-full px-1.5 py-0.5">
                                desabilitada
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-otm-txt-3 mt-0.5">
                            {etapa.operacoesVivas} ativa(s) · {etapa.operacoesTotal} no total
                          </p>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() =>
                              setForm({
                                mode: 'editar',
                                id: etapa.id,
                                label: etapa.label,
                                obrigatoria: etapa.obrigatoria,
                                terminal: etapa.terminal,
                              })
                            }
                            className="p-1.5 rounded-md text-otm-txt-3 hover:text-otm-txt-1 hover:bg-otm-subtle"
                            title="Editar"
                          >
                            <Pencil size={16} />
                          </button>
                          {etapa.ativa ? (
                            <button
                              onClick={() => pedirDesabilitar(etapa)}
                              className="p-1.5 rounded-md text-otm-txt-3 hover:text-amber-600 hover:bg-amber-50"
                              title="Desabilitar"
                            >
                              <EyeOff size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => reabilitar(etapa)}
                              className="p-1.5 rounded-md text-otm-txt-3 hover:text-emerald-600 hover:bg-emerald-50"
                              title="Reabilitar"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => pedirExcluir(etapa)}
                            className="p-1.5 rounded-md text-otm-txt-3 hover:text-red-600 hover:bg-red-50"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Sheet lateral criar/editar */}
      <Sheet open={!!form} onOpenChange={(aberto) => { if (!aberto) setForm(null); }}>
        <SheetContent side="right" className="w-full sm:w-[42%] sm:max-w-none p-0 gap-0 flex flex-col bg-white">
          <div className="shrink-0 px-6 pt-6 pb-4 border-b border-slate-100 pr-12 flex items-center gap-2.5">
            <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-otm-navy/10 text-otm-navy">
              <ListOrdered size={18} />
            </span>
            <div>
              <h2 className="text-base font-semibold text-slate-800">
                {form?.mode === 'criar' ? 'Nova etapa' : 'Editar etapa'}
              </h2>
              <p className="text-xs text-slate-500">{faseAtual.label} · {faseAtual.sub}</p>
            </div>
          </div>

          {form && (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className={LABEL_CLS}>Nome da etapa</label>
                <input
                  className={INPUT_CLS}
                  value={form.label}
                  autoFocus
                  maxLength={120}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Ex.: Documentação enviada ao banco"
                />
                {form.mode === 'criar' && (
                  <p className="text-[11px] text-otm-txt-3 mt-1">
                    A etapa é adicionada ao final da macro-fase; depois arraste para posicionar.
                  </p>
                )}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.terminal}
                  onChange={(e) => setForm({ ...form, terminal: e.target.checked })}
                />
                <span className="text-sm text-slate-700">
                  <strong>Terminal</strong> — última etapa antes de graduar de macro-fase.
                  <span className="block text-[11px] text-otm-txt-3">
                    Exclusiva: marcar esta desmarca a terminal anterior da mesma macro-fase.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.obrigatoria}
                  onChange={(e) => setForm({ ...form, obrigatoria: e.target.checked })}
                />
                <span className="text-sm text-slate-700">
                  <strong>Obrigatória</strong> — exigida para graduar de macro-fase.
                </span>
              </label>
            </div>
          )}

          <div className="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
            <button
              onClick={() => setForm(null)}
              disabled={salvando}
              className="text-sm font-medium text-slate-600 hover:text-slate-800 px-3 py-2 rounded-lg disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={salvarForm}
              disabled={salvando}
              className="text-sm font-semibold text-white px-4 py-2 rounded-lg bg-otm-orange hover:bg-otm-orange-dark transition-colors disabled:opacity-50"
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmModal
        aberto={!!confirm}
        titulo={confirm?.titulo ?? ''}
        mensagem={confirm?.mensagem ?? ''}
        tom={confirm?.tom}
        textoConfirmar={confirm?.textoConfirmar}
        processando={processandoConfirm}
        onConfirmar={executarConfirm}
        onCancelar={() => setConfirm(null)}
      />
    </div>
  );
}
